import { addDays, format, parseISO } from 'date-fns';
import { Appointment, ClientWork, DashboardClient } from './tasks/types';

export const dayKey = (date: Date) => format(date, 'yyyy-MM-dd');

export const formatClock = (time: Appointment['time']) => {
  if (!time) return '-';
  const date = new Date(2000, 0, 1, time.hours, time.minutes);
  return format(date, 'h:mm a');
};

const minutesOf = (time: Appointment['time']) => (time ? time.hours * 60 + time.minutes : Number.POSITIVE_INFINITY);

/** The client's next booked session as a day ("Today", "Tomorrow", "Thu 29 Oct") and a time, or null. */
export function nextSession(client: DashboardClient, now: Date): { day: string; time: string } | null {
  const today = dayKey(now);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const next = client.appointments.find((a) => a.date > today || (a.date === today && minutesOf(a.time) >= nowMinutes));
  if (!next) return null;
  const day = next.date === today ? 'Today' : next.date === dayKey(addDays(now, 1)) ? 'Tomorrow' : format(parseISO(next.date), 'EEE d MMM');
  return { day, time: formatClock(next.time) };
}

/** "Today 11:00 AM", "Tomorrow 7:00 AM" or "Thu 29 Oct, 7:00 AM" for a booked date and time. */
export function slotLabel(slot: { date: string; time: Appointment['time'] }, now: Date): string {
  const time = formatClock(slot.time);
  if (slot.date === dayKey(now)) return `Today ${time}`;
  if (slot.date === dayKey(addDays(now, 1))) return `Tomorrow ${time}`;
  return `${format(parseISO(slot.date), 'EEE d MMM')}, ${time}`;
}

/** "Today 11:00 am", "Tomorrow 7:00 am", "Thu 29 Oct, 7:00 am", or "None booked". */
export function nextSessionLabel(client: DashboardClient, now: Date): string {
  const next = nextSession(client, now);
  if (!next) return 'None booked';
  return next.day === 'Today' || next.day === 'Tomorrow' ? `${next.day} ${next.time}` : `${next.day}, ${next.time}`;
}

export interface ScheduledSession {
  key: string;
  /** The client's mirror record; null for a Mindbody client not in the client list yet (a schedule-only card). */
  client: DashboardClient | null;
  clientId: string;
  /** Display name, set even when `client` is null. */
  clientName: string;
  appointment: Appointment;
  /** The client's open tasks, if any. Always null for a collaboration: those tasks are the lead coach's. */
  work: ClientWork | null;
  past: boolean;
  /** The client is led by another coach; this coach delivers some of their sessions. */
  collaboration: boolean;
  /** From the Mindbody schedule only; null when the day came from the client list. */
  sessionTypeName: string | null;
  durationMin: number | null;
  status: string | null;
  /** False for a late cancel or no-show: shown muted, left out of every count and the Now line. */
  countable: boolean;
}

/** Sessions that still count: the day's real bookings. */
export const countableSessions = (sessions: ScheduledSession[]) => sessions.filter((session) => session.countable);

/**
 * A coach's sessions on one day, in time order: appointments booked with them,
 * for clients they lead and clients they collaborate on.
 */
export function sessionsForDay(
  clients: DashboardClient[],
  workByClient: Map<string, ClientWork>,
  coachId: string,
  day: Date,
  now: Date
): ScheduledSession[] {
  const key = dayKey(day);
  const isToday = key === dayKey(now);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const coach = Number(coachId);
  const sessions: ScheduledSession[] = [];
  clients.forEach((client) => {
    const collaboration = client.coachId !== coachId;
    // Mindbody occasionally holds the same session twice (same client, coach, start and session type): show it once.
    const seen = new Set<number>();
    client.appointments
      .filter((appointment) => appointment.date === key && appointment.coachId === coach)
      .filter((appointment) => {
        const start = minutesOf(appointment.time);
        if (seen.has(start)) return false;
        seen.add(start);
        return true;
      })
      .forEach((appointment, index) => {
        sessions.push({
          key: `${client.docId}-${index}`,
          client,
          clientId: client.clientId,
          clientName: client.name,
          appointment,
          work: collaboration ? null : workByClient.get(client.clientId) ?? null,
          past: isToday && minutesOf(appointment.time) < nowMinutes,
          collaboration,
          sessionTypeName: null,
          durationMin: null,
          status: null,
          countable: true,
        });
      });
  });
  return sortSessions(sessions);
}

/** Time order, then client name. */
export const sortSessions = (sessions: ScheduledSession[]) =>
  sessions.sort((a, b) => minutesOf(a.appointment.time) - minutesOf(b.appointment.time) || a.clientName.localeCompare(b.clientName));

/**
 * "Ignition · week 2 · PT 4/22" for programme clients. Anyone else shows nothing: their programme
 * field holds a pricing option name, and the PT count would be read from that same text.
 */
export const clientMeta = (client: DashboardClient) =>
  client.programme
    ? [client.programmeLabel, client.week ? `week ${client.week}` : null, client.ptTotal !== null && client.ptUsed !== null ? `PT ${client.ptUsed}/${client.ptTotal}` : null]
        .filter(Boolean)
        .join(' · ')
    : '';
